import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';

interface PageHeaderProps {
  /** The screen's icon, e.g. lucide's `GitBranch`. Rendered at a fixed size and colour. */
  icon: LucideIcon;
  title: string;
  subtitle?: string;
  /** Anything that belongs at the right end of the strip: a mode chip, a button, a count. */
  actions?: ReactNode;
  /** Overridden by MasterPageHeader so master screens keep their own test hook. */
  testId?: string;
}

/**
 * The title strip at the top of a screen.
 *
 * Extracted from MasterPageHeader once the setup screens needed it. Three screens there each drew
 * their own: one at `text-xl` in a `p-6` page with an orange icon, one as a `px-6 py-3` bar at
 * `text-base` with a blue one, and the master screens at `px-5 py-3` and `text-sm` with the brand
 * colour. Same element, three sizes, three accent colours, depending on which screen you had
 * navigated to.
 *
 * The icon takes its size and colour from here rather than from the caller, because that is
 * precisely what drifted.
 */
export const PageHeader = ({
  icon: Icon, title, subtitle, actions, testId = 'page-header',
}: PageHeaderProps) => (
  <div
    data-testid={testId}
    className="flex-shrink-0 flex items-center gap-3 px-5 py-3 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
  >
    <Icon size={18} className="text-brand-500 flex-shrink-0" />
    <div className="flex-1 min-w-0">
      <h1 className="text-sm font-bold text-gray-900 dark:text-gray-100 leading-tight">{title}</h1>
      {subtitle && <p className="text-xs text-gray-500 dark:text-gray-400">{subtitle}</p>}
    </div>
    {actions}
  </div>
);
